import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DataProvider, useData } from './DataProvider';
import { getEffectiveViewConfig, loadRunViewConfig, saveRunViewConfig } from './viewSettings';
import DashboardHeader from './components/DashboardHeader';
import DashboardLayout from './components/DashboardLayout';
import ViewSettingsDialog from './components/ViewSettingsDialog';
import { useResizeHandlers } from './hooks/useResizeHandlers';
import LoadingProgressOverlay from './LoadingProgressOverlay';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';

// Component to fetch and set autoinspect data
const AutoInspectDataLoader = ({ appInfo }) => {
  const { setAutoinspectData, setLoadingAutoinspect } = useData();

  useEffect(() => {
    // Clear data immediately when URL changes (before fetching new data)
    setAutoinspectData(null);
    
    const url = appInfo?.autoinspect;
    if (!url) {
      setLoadingAutoinspect(false);
      return;
    }

    let cancelled = false;
    setLoadingAutoinspect(true);

    const fetchAutoinspectData = async () => {
      try {
        const response = await fetch(url);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) {
            setAutoinspectData(data);
            setLoadingAutoinspect(false);
          }
        } else {
          if (!cancelled) {
            setAutoinspectData(null);
            setLoadingAutoinspect(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to fetch autoinspect.json:', error);
          setAutoinspectData(null);
          setLoadingAutoinspect(false);
        }
      }
    };

    fetchAutoinspectData();

    // Cleanup function to cancel fetch if URL changes or component unmounts
    return () => {
      cancelled = true;
      setAutoinspectData(null);
      setLoadingAutoinspect(false);
    };
  }, [appInfo?.autoinspect, setAutoinspectData, setLoadingAutoinspect]);

  return null;
};

// Component to fetch and set file info data
const FileInfoLoader = ({ appInfo }) => {
  const { setFileInfo, setLoadingFiles } = useData();

  useEffect(() => {
    // Clear data immediately when URL changes (before fetching new data)
    setFileInfo([]);
    
    const url = appInfo?.fileinfo;
    if (!url) {
      setLoadingFiles(false);
      return;
    }

    let cancelled = false;
    setLoadingFiles(true);

    const fetchFileInfo = async () => {
      try {
        const response = await fetch(url);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          const arr = Object.values(data);
          if (!cancelled) {
            setFileInfo(arr);
            setLoadingFiles(false);
          }
        } else {
          if (!cancelled) {
            setFileInfo([]);
            setLoadingFiles(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to fetch fileinfo.json:', error);
          setFileInfo([]);
          setLoadingFiles(false);
        }
      }
    };

    fetchFileInfo();

    // Cleanup function to cancel fetch if URL changes or component unmounts
    return () => {
      cancelled = true;
      setFileInfo([]);
      setLoadingFiles(false);
    };
  }, [appInfo?.fileinfo, setFileInfo, setLoadingFiles]);

  return null;
};

// Component to fetch and set copycert data
const CopyCertsLoader = ({ appInfo }) => {
  const { setCopyCerts, setLoadingCopycerts } = useData();

  useEffect(() => {
    // Clear data immediately when URL changes (before fetching new data)
    setCopyCerts([]);
    
    const url = appInfo?.copycert;
    if (!url) {
      setLoadingCopycerts(false);
      return;
    }

    let cancelled = false;
    setLoadingCopycerts(true);

    const fetchCopyCerts = async () => {
      try {
        const response = await fetch(url);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          const arr = Object.values(data);
          if (!cancelled) {
            setCopyCerts(arr);
            setLoadingCopycerts(false);
          }
        } else {
          if (!cancelled) {
            setCopyCerts([]);
            setLoadingCopycerts(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to fetch copycert.json:', error);
          setCopyCerts([]);
          setLoadingCopycerts(false);
        }
      }
    };

    fetchCopyCerts();

    // Cleanup function to cancel fetch if URL changes or component unmounts
    return () => {
      cancelled = true;
      setCopyCerts([]);
      setLoadingCopycerts(false);
    };
  }, [appInfo?.copycert, setCopyCerts, setLoadingCopycerts]);

  return null;
};

// Component to fetch and set bitsinfo data
const BitsInfoLoader = ({ appInfo }) => {
  const { setBitsInfo, setLoadingBitsinfo } = useData();

  useEffect(() => {
    // Clear data immediately when URL changes (before fetching new data)
    setBitsInfo([]);
    
    const url = appInfo?.bitsinfo;
    if (!url) {
      setLoadingBitsinfo(false);
      return;
    }

    let cancelled = false;
    setLoadingBitsinfo(true);

    const fetchBitsInfo = async () => {
      try {
        const response = await fetch(url);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          const arr = Object.values(data);
          if (!cancelled) {
            setBitsInfo(arr);
            setLoadingBitsinfo(false);
          }
        } else {
          if (!cancelled) {
            setBitsInfo([]);
            setLoadingBitsinfo(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to fetch bitsinfo.json:', error);
          setBitsInfo([]);
          setLoadingBitsinfo(false);
        }
      }
    };

    fetchBitsInfo();

    // Cleanup function to cancel fetch if URL changes or component unmounts
    return () => {
      cancelled = true;
      setBitsInfo([]);
      setLoadingBitsinfo(false);
    };
  }, [appInfo?.bitsinfo, setBitsInfo, setLoadingBitsinfo]);

  return null;
};

// Component to fetch and set storyitems data
const StoryItemsLoader = ({ appInfo }) => {
  const { setStoryItems, setLoadingStoryitems } = useData();

  useEffect(() => {
    // Clear data immediately when URL changes (before fetching new data)
    setStoryItems([]);
    
    const url = appInfo?.storyitems;
    if (!url) {
      setLoadingStoryitems(false);
      return;
    }

    let cancelled = false;
    setLoadingStoryitems(true);

    const fetchStoryItems = async () => {
      try {
        const response = await fetch(url);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          const arr = Array.isArray(data) ? data : [];
          // Filter to only Process items (same as StoryItems component)
          const justprocess = arr.filter(si => si.ItemType === "Process");
          if (!cancelled) {
            setStoryItems(justprocess);
            setLoadingStoryitems(false);
          }
        } else {
          if (!cancelled) {
            setStoryItems([]);
            setLoadingStoryitems(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to fetch collect.json:', error);
          setStoryItems([]);
          setLoadingStoryitems(false);
        }
      }
    };

    fetchStoryItems();

    // Cleanup function to cancel fetch if URL changes or component unmounts
    return () => {
      cancelled = true;
      setStoryItems([]);
      setLoadingStoryitems(false);
    };
  }, [appInfo?.storyitems, setStoryItems, setLoadingStoryitems]);

  return null;
};

// Component to load reviewed violations from Firebase with real-time updates
const ReviewedReportLoader = ({ runId }) => {
  const { setReviewedReport, setLoadingReviewedReport, autoinspectData } = useData();

  useEffect(() => {
    // Clear data immediately when runId changes
    setReviewedReport(null);
    
    if (!runId) {
      setLoadingReviewedReport(false);
      return;
    }

    setLoadingReviewedReport(true);

    try {
      // Query the violations subcollection for this run
      const violationsRef = collection(db, 'inspectionReview', runId, 'violations');
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(
        violationsRef,
        (querySnapshot) => {
          const reviewedViolations = [];
          let latestReviewTime = null;

          querySnapshot.forEach((docSnap) => {
            const reviewData = docSnap.data();
            const violation = {
              RuleID: reviewData.RuleID,
              Scenario: reviewData.Scenario,
              ViolationSummary: reviewData.ViolationSummary,
              EvidenceFileNames: reviewData.EvidenceFileNames || [],
              ignore: reviewData.ignore === true,
              lastModified: reviewData.lastModified
            };
            reviewedViolations.push(violation);
            
            // Track latest review time
            if (!latestReviewTime || new Date(reviewData.lastModified) > new Date(latestReviewTime)) {
              latestReviewTime = reviewData.lastModified;
            }
          });

          // Build reviewed report structure
          // If autoinspectData exists, merge reviewed violations with autoinspect violations
          if (autoinspectData?.summarizedInfo?.results) {
            const mergedViolations = [...autoinspectData.summarizedInfo.results];
            
            reviewedViolations.forEach((reviewedViolation) => {
              const index = mergedViolations.findIndex(
                v => v.RuleID === reviewedViolation.RuleID && v.Scenario === reviewedViolation.Scenario
              );
              
              if (index !== -1) {
                // Update existing violation with reviewed data
                mergedViolations[index] = {
                  ...mergedViolations[index],
                  ...reviewedViolation
                };
              } else {
                // Add new violation that doesn't exist in autoinspectData
                mergedViolations.push(reviewedViolation);
              }
            });
            
            const reviewed = {
              baseReportTime: autoinspectData.summarizeTime,
              reviewTime: latestReviewTime || new Date().toISOString(),
              results: mergedViolations
            };
            setReviewedReport(reviewed);
          } else {
            // No autoinspectData, so reviewed violations are the only source
            const reviewed = {
              baseReportTime: null,
              reviewTime: latestReviewTime || new Date().toISOString(),
              results: reviewedViolations
            };
            setReviewedReport(reviewed);
          }
          
          setLoadingReviewedReport(false);
        },
        (error) => {
          console.error('Error loading reviewed violations:', error);
          setReviewedReport(null);
          setLoadingReviewedReport(false);
        }
      );

      // Cleanup listener on unmount or when dependencies change
      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up reviewed violations listener:', error);
      setReviewedReport(null);
      setLoadingReviewedReport(false);
    }
  }, [runId, autoinspectData, setReviewedReport, setLoadingReviewedReport]);

  return null;
};

const VisualStoryTimelineContent = ({ appInfo, runId = null, readOnlyFromUrl = false, hideReadOnlyToggle = false }) => {
  // Get loading states from DataProvider
  const { 
    loading, 
    loadingProgress, 
    loadingFiles, 
    loadingCopycerts, 
    loadingBitsinfo,
    loadingStoryitems, 
    loadingAutoinspect
  } = useData();

  // Read-only mode: init from URL, can be toggled in toolbar; sync from URL when it changes (e.g. navigation)
  const [readOnly, setReadOnly] = useState(readOnlyFromUrl);
  useEffect(() => {
    setReadOnly(readOnlyFromUrl);
  }, [readOnlyFromUrl]);

  // Per-run read-only config loaded from Firestore
  const [readOnlyRunConfig, setReadOnlyRunConfig] = useState(null);
  useEffect(() => {
    if (!runId) { setReadOnlyRunConfig(null); return; }
    let cancelled = false;
    loadRunViewConfig(runId).then((cfg) => {
      if (!cancelled) setReadOnlyRunConfig(cfg);
    });
    return () => { cancelled = true; };
  }, [runId]);

  // Effective view config: picks localStorage prefs (normal) or Firestore run config (read-only)
  const [effectiveViewConfig, setEffectiveViewConfig] = useState(
    () => getEffectiveViewConfig({ readOnly, readOnlyRunConfig })
  );
  useEffect(() => {
    setEffectiveViewConfig(getEffectiveViewConfig({ readOnly, readOnlyRunConfig }));
  }, [readOnly, readOnlyRunConfig]);
  const [viewSettingsDialogOpen, setViewSettingsDialogOpen] = useState(false);
  const handleViewSettingsSave = () => setEffectiveViewConfig(getEffectiveViewConfig({ readOnly, readOnlyRunConfig }));

  // Calculate aggregate loading state - show overlay if any file is loading
  const isAnyLoading = useMemo(() => {
    return loading || loadingFiles || loadingCopycerts || loadingBitsinfo || loadingStoryitems || loadingAutoinspect;
  }, [loading, loadingFiles, loadingCopycerts, loadingBitsinfo, loadingStoryitems, loadingAutoinspect]);

  // State for controlling panel visibility and expansion
  const [appInfoExpanded, setAppInfoExpanded] = useState(false);
  const [fullScreenPanel, setFullScreenPanel] = useState(null); // 'video' | 'inspection' | 'events' | null

  // Escape exits fullscreen panel
  useEffect(() => {
    if (fullScreenPanel == null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setFullScreenPanel(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullScreenPanel]);

  // Custom hook for resize functionality
  const {
    containerRef,
    videoWidth,
    contentHeight,
    handleVerticalMouseDown,
    handleHorizontalMouseDown
  } = useResizeHandlers(appInfoExpanded);

  // Event handlers
  const handleToggleAppInfo = () => setAppInfoExpanded(!appInfoExpanded);

  const handleReadOnlyToggle = (val) => {
    const enabled = !!val;
    setReadOnly(enabled);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (enabled) url.searchParams.set('readOnly', '1');
      else url.searchParams.delete('readOnly');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
    // onReadOnlyChange callback removed – read-only state is managed internally
  };

  return (
    <Box 
      ref={containerRef}
      display="flex" 
      flexDirection="column" 
      width="100%" 
      maxWidth="100%" 
      height="100%"
      minHeight={0}
      sx={{ overflow: 'hidden', position: 'relative' }}
    >
      {/* Loading Overlay */}
      {isAnyLoading && (
        <LoadingProgressOverlay 
          loadingProgress={loadingProgress} 
          loading={isAnyLoading} 
        />
      )}

      {/* Header */}
      <DashboardHeader
        appInfo={appInfo}
        appInfoExpanded={appInfoExpanded}
        onToggleAppInfo={handleToggleAppInfo}
        readOnly={readOnly}
        onReadOnlyToggle={hideReadOnlyToggle ? undefined : handleReadOnlyToggle}
        onOpenViewSettings={hideReadOnlyToggle ? null : () => setViewSettingsDialogOpen(true)}
        fullScreenPanel={fullScreenPanel}
        onExitFullScreen={() => setFullScreenPanel(null)}
        viewConfig={effectiveViewConfig}
      />

      <ViewSettingsDialog
        open={viewSettingsDialogOpen}
        onClose={() => setViewSettingsDialogOpen(false)}
        onSave={handleViewSettingsSave}
        runId={runId}
        appName={appInfo?.appName}
        initialReadOnlyConfig={readOnlyRunConfig}
        onSaveReadOnlyDefaults={async (id, config) => {
          await saveRunViewConfig(id, config);
          // Reload the saved config so it takes effect immediately
          const fresh = await loadRunViewConfig(id);
          setReadOnlyRunConfig(fresh);
        }}
      />

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DashboardLayout
            appInfo={appInfo}
            runId={runId}
            viewConfig={effectiveViewConfig}
            videoWidth={videoWidth}
            contentHeight={contentHeight}
            onVerticalMouseDown={handleVerticalMouseDown}
            onHorizontalMouseDown={handleHorizontalMouseDown}
            fullScreenPanel={fullScreenPanel}
            onFullScreenPanel={setFullScreenPanel}
          />
        </Box>
      </Box>
    </Box>
  );
};

// metadata.json: Executable / Version and, for runs collected since the macOS work, the
// Platform ("windows" | "macos") + PlatformVersion tags every platform branch keys off.
const MetadataLoader = ({ appInfo }) => {
  const { setMetadata } = useData();
  useEffect(() => {
    setMetadata(null);
    const url = appInfo?.metadata;
    if (!url) return;
    let cancelled = false;
    fetch(url)
      .then(res => { if (!res.ok) throw new Error('fetch failed'); return res.json(); })
      .then(data => { if (!cancelled) setMetadata(data); })
      .catch(() => { if (!cancelled) setMetadata(null); });
    return () => { cancelled = true; };
  }, [appInfo?.metadata, setMetadata]);
  return null;
};

const InstallerFileInfoLoader = ({ appInfo }) => {
  const { setInstallerFileInfo } = useData();
  useEffect(() => {
    setInstallerFileInfo(null);
    const url = appInfo?.installerfileinfo;
    if (!url) return;
    let cancelled = false;
    fetch(url)
      .then(res => { if (!res.ok) throw new Error('fetch failed'); return res.json(); })
      .then(data => { if (!cancelled) setInstallerFileInfo(data); })
      .catch(() => { if (!cancelled) setInstallerFileInfo(null); });
    return () => { cancelled = true; };
  }, [appInfo?.installerfileinfo, setInstallerFileInfo]);
  return null;
};

// Minimal shell when appInfo exists but watchlog is not yet available (keeps title bar visible, avoids layout collapse)
const VisualStoryTimelinePlaceholder = ({ appInfo }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Typography variant="h6" component="h1" fontWeight="bold">
        {appInfo?.appName || 'App Inspector'}
      </Typography>
    </Box>
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Typography color="text.secondary">Loading run data…</Typography>
    </Box>
  </Box>
);

const VisualStoryTimeline = ({ appInfo, runId = null, readOnlyFromUrl = false, hideReadOnlyToggle = false, initialVideoTimeSeconds = null }) => {
  if (!appInfo) return null;
  if (!appInfo.watchlog) return <VisualStoryTimelinePlaceholder appInfo={appInfo} />;
  return (
    <DataProvider filePath={appInfo.watchlog} blobPath={appInfo.blobPath} initialSeekTimeSeconds={initialVideoTimeSeconds}>
      <AutoInspectDataLoader appInfo={appInfo} />
      <FileInfoLoader appInfo={appInfo} />
      <CopyCertsLoader appInfo={appInfo} />
      <BitsInfoLoader appInfo={appInfo} />
      <StoryItemsLoader appInfo={appInfo} />
      <InstallerFileInfoLoader appInfo={appInfo} />
      <MetadataLoader appInfo={appInfo} />
      <ReviewedReportLoader runId={runId} />
      <VisualStoryTimelineContent appInfo={appInfo} runId={runId} readOnlyFromUrl={readOnlyFromUrl} hideReadOnlyToggle={hideReadOnlyToggle} />
    </DataProvider>
  );
};

export default VisualStoryTimeline;
